import { useState } from 'react';
import { Link, useLocation, useParams } from 'react-router-dom';
import { useStories } from '../../hooks/useStories';
import { useStreams } from '../../hooks/useStreams';
import { useRundowns } from '../../hooks/useRundowns';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Layers, ChevronRight, ListMusic, Calendar, ChevronLeft } from 'lucide-react';

// Get today's date in ET timezone
function getTodayET(): string {
  const now = new Date();
  const etDate = new Date(now.toLocaleString('en-US', { timeZone: 'America/New_York' }));
  return etDate.toISOString().split('T')[0];
}

// Format date for display
function formatDateDisplay(date: string): string {
  const today = getTodayET();
  if (date === today) return "Today's Stories";

  const d = new Date(date + 'T12:00:00');
  const options: Intl.DateTimeFormatOptions = { weekday: 'short', month: 'short', day: 'numeric' };
  return d.toLocaleDateString('en-US', options);
}

export function Sidebar() {
  const location = useLocation();
  const { orgSlug } = useParams<{ orgSlug: string }>();
  const [selectedDate, setSelectedDate] = useState<string>(getTodayET());

  const { data: storiesData, isLoading: storiesLoading, error: storiesError } = useStories({
    date: selectedDate,
    limit: 50
  });
  const { data: streamsData, isLoading: streamsLoading } = useStreams();
  const { data: rundownsData, isLoading: rundownsLoading } = useRundowns({ limit: 5 });

  const stories = storiesData?.stories || [];
  const streams = streamsData?.streams || [];
  const rundowns = rundownsData?.rundowns || [];

  // Build org-prefixed paths
  const orgPrefix = orgSlug ? `/${orgSlug}` : '';

  // Date navigation handlers
  const goToPreviousDay = () => {
    const d = new Date(selectedDate + 'T12:00:00');
    d.setDate(d.getDate() - 1);
    setSelectedDate(d.toISOString().split('T')[0]);
  };

  const goToNextDay = () => {
    const d = new Date(selectedDate + 'T12:00:00');
    d.setDate(d.getDate() + 1);
    const nextDate = d.toISOString().split('T')[0];
    // Don't go past today
    if (nextDate <= getTodayET()) {
      setSelectedDate(nextDate);
    }
  };

  const goToToday = () => {
    setSelectedDate(getTodayET());
  };

  const isToday = selectedDate === getTodayET();

  return (
    <aside className="w-64 flex-shrink-0">
      <div className="sticky top-20 h-[calc(100vh-6rem)] flex flex-col space-y-4">
        {/* Streams section */}
        <div className="bg-white rounded-lg shadow p-4">
          <Link
            to={`${orgPrefix}/streams`}
            className="flex items-center justify-between text-sm font-semibold text-gray-500 uppercase tracking-wide mb-3 hover:text-gray-700"
          >
            <span className="flex items-center gap-1">
              <Layers className="w-4 h-4" />
              Streams
            </span>
            <ChevronRight className="w-4 h-4" />
          </Link>

          {streamsLoading ? (
            <div className="text-sm text-gray-400">Loading...</div>
          ) : streams.length === 0 ? (
            <div className="text-sm text-gray-400">No active streams</div>
          ) : (
            <nav className="space-y-1">
              {streams.slice(0, 5).map((stream) => {
                const isActive = location.pathname === `${orgPrefix}/stream/${stream.id}`;
                return (
                  <Link
                    key={stream.id}
                    to={`${orgPrefix}/stream/${stream.id}`}
                    className={`block px-2 py-1.5 text-sm rounded transition-colors ${
                      isActive
                        ? 'bg-purple-50 text-purple-700 font-medium'
                        : 'text-gray-700 hover:bg-gray-50'
                    }`}
                  >
                    <span className="line-clamp-2">{stream.title}</span>
                    <span className="text-xs text-gray-500">{stream.storyCount} stories</span>
                  </Link>
                );
              })}
              {streams.length > 5 && (
                <Link
                  to={`${orgPrefix}/streams`}
                  className="block px-2 py-1 text-xs text-gray-500 hover:text-gray-700"
                >
                  View all {streams.length} streams →
                </Link>
              )}
            </nav>
          )}
        </div>

        {/* Rundowns section */}
        <div className="bg-white rounded-lg shadow p-4">
          <Link
            to={`${orgPrefix}/rundowns`}
            className="flex items-center justify-between text-sm font-semibold text-gray-500 uppercase tracking-wide mb-3 hover:text-gray-700"
          >
            <span className="flex items-center gap-1">
              <ListMusic className="w-4 h-4" />
              Rundowns
            </span>
            <ChevronRight className="w-4 h-4" />
          </Link>

          {rundownsLoading ? (
            <div className="text-sm text-gray-400">Loading...</div>
          ) : rundowns.length === 0 ? (
            <div className="text-sm text-gray-400">No rundowns yet</div>
          ) : (
            <nav className="space-y-1">
              {rundowns.map((rundown) => {
                const isActive = location.pathname === `${orgPrefix}/rundown/${rundown.id}`;
                return (
                  <Link
                    key={rundown.id}
                    to={`${orgPrefix}/rundown/${rundown.id}`}
                    className={`block px-2 py-1.5 text-sm rounded transition-colors ${
                      isActive
                        ? 'bg-blue-50 text-blue-700 font-medium'
                        : 'text-gray-700 hover:bg-gray-50'
                    }`}
                  >
                    <span className="line-clamp-1">{rundown.title}</span>
                    <span className="text-xs text-gray-500">{rundown.storyCount} stories</span>
                  </Link>
                );
              })}
            </nav>
          )}
        </div>

        {/* Stories section with date navigation */}
        <div className="bg-white rounded-lg shadow p-4 flex-1 min-h-0 flex flex-col">
          <div className="flex items-center justify-between mb-3 flex-shrink-0">
            <h2 className="text-sm font-semibold text-gray-500 uppercase tracking-wide">
              {formatDateDisplay(selectedDate)}
            </h2>
            <div className="flex items-center gap-1">
              <button
                onClick={goToPreviousDay}
                className="p-1 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded"
                title="Previous day"
              >
                <ChevronLeft className="w-4 h-4" />
              </button>
              <button
                onClick={goToToday}
                className={`p-1 rounded ${isToday ? 'text-blue-600' : 'text-gray-400 hover:text-gray-600 hover:bg-gray-100'}`}
                title="Go to today"
                disabled={isToday}
              >
                <Calendar className="w-4 h-4" />
              </button>
              <button
                onClick={goToNextDay}
                className={`p-1 rounded ${isToday ? 'text-gray-300 cursor-not-allowed' : 'text-gray-400 hover:text-gray-600 hover:bg-gray-100'}`}
                title="Next day"
                disabled={isToday}
              >
                <ChevronRight className="w-4 h-4" />
              </button>
            </div>
          </div>

          {storiesLoading ? (
            <div className="text-sm text-gray-400">Loading...</div>
          ) : storiesError ? (
            <div className="text-sm text-red-400">Error loading stories</div>
          ) : stories.length === 0 ? (
            <div className="text-sm text-gray-400">No stories for this date</div>
          ) : (
            <ScrollArea className="flex-1 min-h-0">
              <nav className="space-y-1 pr-3">
                {stories.map((story) => {
                  const isActive = location.pathname === `${orgPrefix}/story/${story.id}`;
                  return (
                    <Link
                      key={story.id}
                      to={`${orgPrefix}/story/${story.id}`}
                      className={`block px-2 py-1.5 text-sm rounded transition-colors ${
                        isActive
                          ? 'bg-blue-50 text-blue-700 font-medium'
                          : 'text-gray-700 hover:bg-gray-50'
                      }`}
                    >
                      <span className="line-clamp-2">{story.title}</span>
                    </Link>
                  );
                })}
              </nav>
            </ScrollArea>
          )}
        </div>
      </div>
    </aside>
  );
}
